/**
 * Renderizador de formulário multi-step para Oferta
 * Step 1: Dados básicos + LGPD
 * Step 2: Dados complementares (CPF, CEP, etc)
 */

import { escapeHtml, getOfferTitle, getOfferDescription, getOfferImageUrl } from '../helpers/ofertaFormularioHelpers.js';
import { renderCheckboxes } from './sharedCheckboxes.js';

/**
 * Renderiza Step 1: Nome, Email, Telefone, Checkboxes
 */
export function renderStepLayout(config, ofertaData) {
    const { cssPrefix } = config;
    // Obtem dados formatados
    const imageUrl = getOfferImageUrl(ofertaData);
    const offerTitle = getOfferTitle(ofertaData);
    const offerDescription = getOfferDescription(ofertaData);

    return `
        <div class="${cssPrefix}-wrapper">
            ${config.textHeader ? `<div class="${cssPrefix}-header">${escapeHtml(config.textHeader)}</div>` : ''}
            
            <div class="${cssPrefix}-col1">
                <div class="${cssPrefix}-titulos" id="${cssPrefix}-titulos">
                    <h2>${escapeHtml(config.title0km)}</h2>
                    ${offerTitle ? `<h3 style="color:${config.color}">${escapeHtml(offerTitle)}</h3>` : ''}
                    ${offerDescription ? `<p>${escapeHtml(offerDescription)}</p>` : ''}
                </div>
                ${imageUrl ? `
                    <figure class="${cssPrefix}-image ${!config.isImageMobile ? 'hide-mobile' : ''}" id="${cssPrefix}-image">
                        <img src="${imageUrl}" alt="Oferta" loading="lazy" decoding="async" />
                    </figure>
                ` : ''}
            </div>

            <div class="${cssPrefix}-col2">
                <div id="${cssPrefix}-step-content"></div>
                <div class="${cssPrefix}-message" id="${cssPrefix}-message"></div>
            </div>
        </div>
    `;
}

/**
 * Renderiza Step 1: Nome, Email, Telefone, Checkboxes
 */
export function renderStep1(config, ofertaData) {
    const { cssPrefix } = config;

    // Determina textos dos checkboxes
    const hasOptInWidget = ofertaData?.optin_widget || false;
    const textAceite = ofertaData?.aceptText || '';
    const textCiente = ofertaData?.textAware || '';
    const textDeclaro = ofertaData?.textDeclare || '';

    return `
        <form class="${cssPrefix}-form" id="${cssPrefix}-form-step1" novalidate>
            <h4>${escapeHtml(config.titleForm)}</h4>
            
            <!-- Indicador de Progresso Step 1 -->
            <div class="${cssPrefix}-progress-indicator">
                <div class="${cssPrefix}-progress-step ${cssPrefix}-progress-step-active">
                    <div class="${cssPrefix}-progress-circle">1</div>
                    <span>Dados básicos</span>
                </div>
                <div class="${cssPrefix}-progress-line"></div>
                <div class="${cssPrefix}-progress-step">
                    <div class="${cssPrefix}-progress-circle">2</div>
                    <span>Dados complementares</span>
                </div>
            </div>
            
            <input type="text" name="name" id="${cssPrefix}-name" placeholder="Nome*" />
            <div id="${cssPrefix}-name-error" class="${cssPrefix}-error"></div>
            
            <input type="email" name="email" id="${cssPrefix}-email" placeholder="E-mail*" />
            <div id="${cssPrefix}-email-error" class="${cssPrefix}-error"></div>
            
            <input type="tel" name="phone" id="${cssPrefix}-phone" placeholder="Telefone*" maxlength="15" />
            <div id="${cssPrefix}-phone-error" class="${cssPrefix}-error"></div>
            
            <div class="${cssPrefix}-checkbox-group" id="${cssPrefix}-checkboxes">
                ${renderCheckboxes(cssPrefix, hasOptInWidget, textAceite, textCiente, textDeclaro, ofertaData?.optin_widget)}
            </div>

            <p class="${cssPrefix}-required-note">Campos marcados com * são obrigatórios.</p>
            
            <button type="submit" aria-label="Continuar" id="${cssPrefix}-btn-step1">Continuar</button>
        </form>
    `;
}

/**
 * Renderiza Step 2: Campos complementares (CPF, CEP, Data de Nascimento)
 * @param {Object} config
 * @param {Object} ofertaData
 * @param {{noCep?: boolean}} [options] - noCep=true (form-no-cep + LP de Interesse):
 *   remove CEP por completo e força Estado/Cidade sempre visíveis/obrigatórios,
 *   independente de isOfferlp/hidden_state_city/zipCode.
 */
export function renderStep2(config, ofertaData, options = {}) {
    const { cssPrefix } = config;
    const { noCep = false } = options;

    const isOfferLp = ofertaData?.isOfferlp === true;
    const hasZipCode = !noCep && (isOfferLp || ofertaData?.zipCode === true);
    const hasBirthDate = ofertaData?.birthDate === true;
    const showQuestions = ofertaData?.show_questions === true;
    const hideStateCity = !noCep && (
        isOfferLp || (
            ofertaData?.hidden_state_city === true ||
            ofertaData?.hiddenStateCity === true
        )
    );

    return `
        <form class="${cssPrefix}-form" id="${cssPrefix}-form-step2" novalidate>
            <h4>${escapeHtml(config.titleForm)}</h4>
            
            <!-- Indicador de Progresso Step 2 -->
            <div class="${cssPrefix}-progress-indicator">
                <div class="${cssPrefix}-progress-step ${cssPrefix}-progress-step-active">
                    <div class="${cssPrefix}-progress-circle">1</div>
                    <span>Dados básicos</span>
                </div>
                <div class="${cssPrefix}-progress-line ${cssPrefix}-progress-line-active"></div>
                <div class="${cssPrefix}-progress-step ${cssPrefix}-progress-step-active">
                    <div class="${cssPrefix}-progress-circle">2</div>
                    <span>Dados complementares</span>
                </div>
            </div>
            
            <input type="text" name="cpf" id="${cssPrefix}-cpf" placeholder="CPF*" maxlength="14" />
            <div id="${cssPrefix}-cpf-error" class="${cssPrefix}-error"></div>
            
            ${hasBirthDate ? `
                <div class="${cssPrefix}-birthdate-wrapper">
                    <input type="date" name="birthDate" id="${cssPrefix}-birthdate" />
                    <label for="${cssPrefix}-birthdate" class="${cssPrefix}-birthdate-placeholder">Data de nascimento *</label>
                </div>
                <div id="${cssPrefix}-birthdate-error" class="${cssPrefix}-error"></div>
            ` : ''}
            
            ${hasZipCode ? `
                <input type="text" name="zipCode" id="${cssPrefix}-cep" placeholder="CEP*" />
                <div id="${cssPrefix}-cep-error" class="${cssPrefix}-error"></div>
            ` : ''}

            ${!hideStateCity ? `
                <div class="${cssPrefix}-state-city-row">
                    <select name="state" id="${cssPrefix}-state" class="${cssPrefix}-select ${cssPrefix}-select-half">
                        <option value="">Estado*</option>
                    </select>
                    <select name="city" id="${cssPrefix}-city" class="${cssPrefix}-select" disabled>
                        <option value="">Cidade*</option>
                    </select>
                </div>
                <div class="${cssPrefix}-state-city-errors">
                    <div id="${cssPrefix}-state-error" class="${cssPrefix}-error ${cssPrefix}-location-error"></div>
                    <div id="${cssPrefix}-city-error" class="${cssPrefix}-error ${cssPrefix}-location-error"></div>
                </div>
            ` : ''}
            
            ${renderLpQuestions(config, ofertaData)}

            <p class="${cssPrefix}-required-note">Campos marcados com * são obrigatórios.</p>
            
            <div class="${cssPrefix}-step-buttons">
                <button type="submit" class="${cssPrefix}-btn-submit" id="${cssPrefix}-btn-step2">Finalizar cadastro</button>
            </div>
        </form>
    `;
}

/**
 * Renderiza perguntas específicas para LPs de interesse
 */
function renderLpQuestions(config, ofertaData) {
    const { cssPrefix } = config;

    // Matriz de comportamento do stepTwo.tsx:
    // isOfferLP | show_questions | Renderiza?
    // true      | true          | ❌ Não
    // true      | false         | ❌ Não
    // false     | true          | ❌ Não
    // false     | false         | ✅ Sim
    const isOfferLp = ofertaData?.isOfferlp === true;
    const showQuestions = ofertaData?.show_questions === true;

    // Só renderiza quando: !isOfferLP && !show_questions
    if (isOfferLp || showQuestions) {
        return '';
    }

    // Obtém o slug da oferta
    const slug = ofertaData?.slug || '';
    let questionsHtml = '';

    // Campos do LpInteresseFields - Modelos de interesse (multiselect compacto)
    if (ofertaData?.generic_models && Array.isArray(ofertaData.generic_models) && ofertaData.generic_models.length > 0) {
        // Agrupa modelos por montadora
        const grouped = ofertaData.generic_models.reduce((acc, model) => {
            const brand = model.montadora?.name || 'Outros';
            if (!acc[brand]) acc[brand] = [];
            acc[brand].push(model);
            return acc;
        }, {});

        const modelosLabel = ofertaData.generic_models.length > 1
            ? 'Quais modelos você tem interesse?*'
            : 'Qual modelo você tem interesse?*';

        questionsHtml += `
            <div class="${cssPrefix}-question">
                <label class="${cssPrefix}-question-label" for="${cssPrefix}-modelos-trigger">${modelosLabel}</label>
                <div class="${cssPrefix}-multiselect" id="${cssPrefix}-modelos-multiselect">
                    <button
                        type="button"
                        class="${cssPrefix}-multiselect-trigger is-placeholder"
                        id="${cssPrefix}-modelos-trigger"
                        aria-haspopup="listbox"
                        aria-expanded="false"
                        aria-controls="${cssPrefix}-modelos-dropdown"
                    >
                        <span class="${cssPrefix}-multiselect-trigger-text">Selecione os modelos*</span>
                    </button>
                    <div
                        class="${cssPrefix}-multiselect-dropdown"
                        id="${cssPrefix}-modelos-dropdown"
                        role="listbox"
                        aria-multiselectable="true"
                        hidden
                    >
                        <div class="${cssPrefix}-models-group">
        `;

        Object.entries(grouped).forEach(([brand, models]) => {
            questionsHtml += `<div class="${cssPrefix}-brand-group">`;
            models.forEach(model => {
                const modelId = `modelosInteresse-${model.documentId}`;
                const label = brand && brand !== 'undefined' ? `${brand} | ${model.title}` : model.title;
                questionsHtml += `
                    <label class="${cssPrefix}-checkbox-label" role="option">
                        <input type="checkbox" name="modelosInteresse" value="${model.documentId}" id="${cssPrefix}-${modelId}" />
                        <span class="${cssPrefix}-checkbox-text">${escapeHtml(label)}</span>
                    </label>
                `;
            });
            questionsHtml += `</div>`;
        });

        questionsHtml += `
                        </div>
                    </div>
                </div>
                <div id="${cssPrefix}-modelos-interesse-error" class="${cssPrefix}-error"></div>
            </div>
        `;
    }

    // Campo "Quando tem interesse em comprar"
    questionsHtml += `
        <div class="${cssPrefix}-question">
            <label class="${cssPrefix}-question-label">Quando tem interesse em comprar?*</label>
            <select name="quandoComprar" id="${cssPrefix}-quando-comprar" class="${cssPrefix}-select">
                <option value="">Selecione uma opção*</option>
                <option value="Em até 30 dias">Em até 30 dias</option>
                <option value="Em até 60 dias">Em até 60 dias</option>
                <option value="Em até 90 dias">Em até 90 dias</option>
                <option value="Estou apenas pesquisando">Estou apenas pesquisando</option>
            </select>
            <div id="${cssPrefix}-quando-comprar-error" class="${cssPrefix}-error"></div>
        </div>
    `;

    // Pergunta 1: Área de plantio para colheitadeiras-case-ih e tratores-case-ih
    if (['colheitadeiras-case-ih', 'tratores-case-ih'].includes(slug)) {
        questionsHtml += `
            <div class="${cssPrefix}-question">
                <label class="${cssPrefix}-question-label">Qual é o tamanho aproximado da sua propriedade ou área de plantio?*</label>
                <select name="plantingArea" id="${cssPrefix}-planting-area" class="${cssPrefix}-select">
                    <option value="">Selecione a área de plantio*</option>
                    <option value="ate_20">Até 20 hectares</option>
                    <option value="acima_20">Acima de 20 hectares</option>
                </select>
                <div id="${cssPrefix}-planting-area-error" class="${cssPrefix}-error"></div>
            </div>
        `;
    }

    // Pergunta 2: Potência do trator para tratores-case-ih
    if (slug === 'tratores-case-ih') {
        questionsHtml += `
            <div class="${cssPrefix}-question">
                <label class="${cssPrefix}-question-label">Qual potência você busca adquirir?*</label>
                <select name="tractorPower" id="${cssPrefix}-tractor-power" class="${cssPrefix}-select">
                    <option value="">Selecione a potência*</option>
                    <option value="144">144</option>
                    <option value="157">157</option>
                    <option value="167">167</option>
                    <option value="182">182</option>
                    <option value="197">197</option>
                    <option value="213">213</option>
                    <option value="234">234</option>
                </select>
                <div id="${cssPrefix}-tractor-power-error" class="${cssPrefix}-error"></div>
            </div>
        `;
    }

    return questionsHtml;
}
